/**
 * Modern 现代模板 - 单页
 * 
 * 风格特点：
 * - 居中内容区域，最大宽度阅读体验
 * - 干净的排版
 * - 蓝色点缀
 */

import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { getSinglePage } from "@/utils/api";
import { useTemplateColors } from "@/hooks/useTemplateColors";

export default function ModernSinglePage() {
  const { slug } = useParams<{ slug: string }>();
  const [page, setPage] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const colors = useTemplateColors();

  useEffect(() => {
    if (!slug) return;
    setLoading(true);

    getSinglePage(slug)
      .then((data) => setPage(data))
      .catch(() => null)
      .finally(() => setLoading(false));
  }, [slug]);

  // 加载中骨架屏（使用 Tailwind 固定蓝色渐变，避免引用外部变量）
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <section className="bg-gradient-to-r from-blue-500 to-blue-600 py-20">
          <div className="max-w-3xl mx-auto px-6 text-center animate-pulse">
            <div className="w-48 h-7 bg-white/30 rounded-lg mx-auto" />
            <div className="w-12 h-1 bg-white/30 rounded-full mx-auto mt-5" />
          </div>
        </section>
        <div className="py-12 max-w-3xl mx-auto px-6">
          <div className="animate-pulse space-y-4 p-10 bg-white rounded-2xl border border-gray-100">
            {[...Array(5)].map((_, i) => (
              <div key={i} className={`bg-gray-200 rounded ${i === 0 ? 'h-6 w-2/3' : i === 4 ? 'h-4 w-1/3' : 'h-4 w-full'}`} />
            ))}
          </div>
        </div>
      </div>
    );
  }

  // 页面未找到
  if (!page) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center px-4">
          <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center rounded-2xl bg-red-50">
            <i className="ri-file-damage-line text-3xl text-red-400" />
          </div>
          <h1 className="text-xl font-bold text-gray-800 mb-2">页面未找到</h1>
          <p className="text-sm text-gray-500 mb-6">您访问的单页不存在或已被删除</p>
          <Link to="/" className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-blue-500 to-blue-600 text-white text-sm font-medium rounded-xl hover:opacity-90 transition-opacity cursor-pointer">
            <i className="ri-home-4-line" />
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />

      {/* 页面头部 */}
      <section className={`bg-gradient-to-r ${colors.headerGradient} py-14 md:py-20`}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          {/* 面包屑 */}
          <nav className={`flex items-center justify-center gap-1.5 text-sm ${colors.textOnDark} mb-5`}>
            <Link to="/" className="hover:text-white transition-colors">首页</Link>
            <i className="ri-arrow-right-s-line text-xs opacity-60" />
            <span className="text-white font-medium">{page.title}</span>
          </nav>

          <h1 className="text-2xl md:text-4xl font-extrabold text-white leading-tight">
            {page.title}
          </h1>

          {/* 装饰线 */}
          <div className={`w-16 h-1 bg-gradient-to-r ${colors.decoratorGradient} rounded-full mx-auto mt-5`} />
        </div>
      </section>

      {/* 内容区 */}
      <article className="py-12">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <div className="bg-white rounded-2xl p-6 md:p-10 border border-gray-100 shadow-sm">
            <div className={`prose prose-lg max-w-none prose-headings:text-gray-800 prose-p:text-gray-600 prose-p:leading-loose ${colors.proseLink} prose-strong:text-gray-800`}>
              {/* 渲染 HTML 内容 */}
              {page.content ? (
                <div dangerouslySetInnerHTML={{ __html: page.content }} />
              ) : (
                <p className="text-gray-400 text-center py-8">暂无内容</p>
              )}
            </div>
          </div>

          {/* 返回首页 */}
          <div className="mt-8 text-center">
            <Link to="/" className={`inline-flex items-center gap-2 text-sm font-medium ${colors.linkText} ${colors.linkHoverText} transition-colors group`}>
              <i className="ri-arrow-left-line group-hover:-translate-x-1 transition-transform" />
              返回首页
            </Link>
          </div>
        </div>
      </article>

      <Footer />
    </div>
  );
}

